<template>
<div>
	<div class="d-flex align-items-center justify-content-between mb-3">
		<h3 class="mb-0 mr-2">Financial Summary</h3>

		<field :params="{type:'btn-group',buttons:[
				{text: 'Summary',value:'summary'},
				{text: 'Detailed', value: 'detailed'}
			],tabStyling:true,disableNull:true}"
				 v-model="activeTab">
		</field>
	</div>

	<template v-if="activeTab == 'summary'">
		<h4>Summary</h4>

		<div class="row">
			<div class="col-lg-6">
				<table-list
					:fields="[
						{label: '',key:'elementLabel'},
						{label: 'Cost',key:'elementCost',align:'right'},
						{label: 'Adjustment Value',key:'elementAdjustmentValue',align:'right'},
						{label: 'Markup Value',key:'elementMarkupValue',align:'right'},
						{label: 'Total Value',key:'elementValue',align:'right'},
						{label: 'Margin',key:'elementMargin',align:'right'},
					]"
					:records="bidSummaryRows"
					:hide-record-count="true">
				</table-list>

				<div class="text-muted"><small>Rounding errors may be present between Total Value and the sum of Cost + Adj. Value + Markup Value. Total value should be taken as correct.</small></div>

				<div class="d-flex justify-content-end totals-widgets-container">
					<widget class="ml-2">
						<strong>Total Cost</strong>
						<div class="totals-value">${{ totalCost.toCurrency() }}</div>
					</widget>
					<widget class="ml-2">
						<strong>Total Value</strong>
						<div class="totals-value">${{ totalValue.toCurrency() }}</div>
					</widget>
					<widget class="ml-2">
						<strong>Gross Profit</strong>
						<div class="totals-value">${{ totalProfit.toCurrency() }}</div>
					</widget>
					<widget class="ml-2">
						<strong>Margin</strong>
						<div class="totals-value" :class="{
							'text-success' : totalMargin > 10, 
							'text-warning' : totalMargin <= 10 && totalMargin > 0,
							'text-danger' : totalMargin <= 0
						}">{{ totalMargin.toFixed(1) }}%</div>
					</widget>
					
				</div>

				<chart
				  type="bar"
				  :labels="[
					'Cost',
					'Adjustments',
					'Markup',
					'Additional Factors',
					'Selling Price'
				  ]"
				  :datasets="waterfallDatasets"
				  :options="waterfallOptions">
				</chart>

			</div>
			<div class="col-lg-6">

				<template v-if="totalCost">
					
					<h5 class="text-center">Cost Breakdown</h5>
					<chart key="costs-chart"
						type="pie"
						:labels="['Materials', 'Labour', 'Equipment', 'Supplier Quotes', 'Additional Factors']"
						:data="bidSummaryRows.map(el => el.elementCostRaw)"
						:background-color="[
							'#7C61F3', // Brand violet (anchor)
							'#5FB3B3', // Teal (harmonizes with aqua)
							'#9A8CF2', // Soft lavender (violet-adjacent)
							'#F2C94C', // Warm muted gold (contrast without shouting)
							'#6B7280'  // Slate gray (neutral stabilizer)
						]"
						:border-width="1"
						:options="pieOptions"
						style="max-height: 180px;">
					</chart>
					<hr>
					<h5 class="text-center">Selling Breakdown</h5>
					<chart
						key="values-chart"
						type="pie"
						:labels="['Materials', 'Labour', 'Equipment', 'Supplier Quotes', 'Additional Factors']"
						:data="bidSummaryRows.map(el => el.elementValueRaw)"
						:background-color="[
							'#7C61F3', // Brand violet (anchor)
							'#5FB3B3', // Teal (harmonizes with aqua)
							'#9A8CF2', // Soft lavender (violet-adjacent)
							'#F2C94C', // Warm muted gold (contrast without shouting)
							'#6B7280'  // Slate gray (neutral stabilizer)
						]"
						:border-width="1"
						:options="pieOptions"
						style="max-height: 180px;">
					</chart>
					<hr>
					<h5 class="text-center">Cost & Gross Profit</h5>
					<chart
						type="pie"
						:labels="['Cost', 'Gross Profit']"
						:data="[totalCost, totalProfit]"
						:background-color="['#7c61f3', '#B2E5E1']"
						:border-width="1"
						:options="pieOptions"
						style="max-height: 180px;">
					</chart>
				</template>
			</div>
		</div>
	</template>
	<template v-else-if="activeTab == 'detailed'">
		<h4>Detailed</h4>

		<project-bid-summary
			:bid-prop="bid"
			:hide-header="true"
			:hide-description="false"
			:hide-spec-number="true"
			:show-detailed-values="true">
		</project-bid-summary>
	</template>
</div>
</template>

<script>
import TableList from "@/components/TableList"
import Widget from "@/components/Widget"
import Chart from "@/components/Chart"
import Field from "@/components/Field"
import ProjectBidSummary from "@/pages/project/project_tabs/bidding/components/ProjectBidSummary"

import ProjectBidMixins from "@/mixins/ProjectBidMixins.js"


export default {
	name: "ProjectBidFinancialSummaryTab",
	components: {
		Widget,
		TableList,
		Field,
		Chart,
		ProjectBidSummary
	},
	mixins: [ProjectBidMixins],
	props: ["bidId"],
	data() {
		return {
			activeTab: 'summary',
			pieOptions: {
				responsive: true,
				maintainAspectRatio: false,
				legend: { position: 'bottom' },
				tooltips: {
					callbacks: {
						label: function (tooltipItem, data) {
						const label = data.labels[tooltipItem.index] || ''
						const value = data.datasets[0].data[tooltipItem.index] || 0
						return `${label}: $${Number(value).toLocaleString()}`
						}
					}
				}
			}
		}
	},
	watch: {
		bidId() {
			this.fetchAllComponents(this.bidId)
		}
	},
	computed: {
		totalCost() {
			//console.log(JSON.stringify(this.bidComponentsNested))

			let elements = ["materials","equipment","labour","supplier-quotes",'additional-factors']

			let totalCost = 0

			elements.forEach(key => {
				let cost = this.flattenedRecordsFromNested(key).reduce((acc,item) => acc + +item.totalBaseRaw,0)

				if (!isNaN(cost))
					totalCost += cost.round(2)
			})

			return totalCost
		},
		totalPriceAdjustment() {

			return this.bidComponentsNested.reduce((acc,item) => item.totalComponentAdjustedValueRaw + acc,0)
		},
		totalMarkup() {
			return this.bidComponentsNested.reduce((acc,item) => item.totalComponentMarkupValueRaw + acc,0)
		},
		totalAdditionalFactors() {
			return this.bidSummaryRows[4].elementValueRaw
		},
		totalValue() {
			return this.bidComponentsNested.reduce((acc,topComp) => acc + topComp.totalPriceRaw,0)
		},
		totalMargin() {
			return (this.totalValue - this.totalCost) / this.totalValue * 100
		},
		totalProfit() {
			return this.totalValue - this.totalCost
		},
		waterfallDatasets() {

			const cost = this.totalCost
			const adj  = this.totalPriceAdjustment
			const mark = this.totalMarkup
			const af   = this.totalAdditionalFactors

			return [
				// OFFSET (invisible)
				{
				  label: 'Offset',
				  data: [
					0,
					cost,
					cost + adj,
					cost + adj + mark,
					0
				  ],
				  backgroundColor: 'rgba(0,0,0,0)',
				  borderWidth: 0,
				  stack: 'waterfall'
				},

				// VISIBLE BARS
				{
				  label: 'Amount',
				  data: [
					cost,
					adj,
					mark,
					af,
					this.totalValue
				  ],
				  backgroundColor: [
					'#7C61F3', // Cost (brand purple)
					'#0EA5E9', // Adjustments (sky blue – distinct from Labour teal in pie charts)
					'#F4A261', // Markup (warm amber – much better than red)
					'#9A8CF2', // Additional Factors (lighter purple)
					'#2ECC71'  // Selling price (green)
				  ],
				  stack: 'waterfall'
				}
			]
		},
		waterfallOptions() {
		  return {
			responsive: true,
			scales: {
			  xAxes: [{
				stacked: true
			  }],
			  yAxes: [{
				stacked: true,
				ticks: {
				  callback: v => '$' + v.toLocaleString()
				}
			  }]
			},
			tooltips: {
			  callbacks: {
				label: (tooltipItem, data) => {
				  const value = tooltipItem.yLabel
				  return '$' + value.toLocaleString()
				}
			  }
			},
			legend: { display: false }
		  }
		},
		bidSummaryRows() {

			let elements = ["materials","equipment","labour","supplier-quotes",'additional-factors']

			let elementValues = {}
			let elementCosts = {}
			let elementMargins = {}
			let elementAdjustmentValues = {}
			let elementMarkupValues = {}
			let totalValue = 0
			let totalCost = 0
			let totalAdjustment = 0
			let totalMarkup = 0
			let totalMargin = 0

			elements.forEach(key => {
				let records = this.flattenedRecordsFromNested(key)

				let cost = records.reduce((acc,item) => acc + +item.totalBaseRaw,0)

				if (!isNaN(cost))
					totalCost += cost.round(2)

				elementCosts[key] = cost

				let value = records.reduce((acc,item) => acc + +item.totalRaw,0)

				if (!isNaN(value))
					totalValue += value.round(2)

				elementValues[key] = value

				// Adjustment value = base * (price_adjustment - 1); additional-factors have no adjustment
				let adjustmentValue = 0
				if (key !== 'additional-factors') {
					adjustmentValue = records.reduce((acc, item) => {
						let base = +item.totalBaseRaw || 0
						let adj = (+item.price_adjustment || 1) - 1
						return acc + (base * adj).round(2)
					}, 0)
				}
				elementAdjustmentValues[key] = adjustmentValue
				totalAdjustment += adjustmentValue

				// Markup value = base * price_adjustment * (markup - 1); additional-factors have no markup
				let markupValue = 0
				if (key !== 'additional-factors') {
					markupValue = records.reduce((acc, item) => {
						let base = +item.totalBaseRaw || 0
						let adj = +item.price_adjustment || 1
						let mu = (+item.markup || 1) - 1
						return acc + (base * adj * mu).round(2)
					}, 0)
				}
				elementMarkupValues[key] = markupValue
				totalMarkup += markupValue

				let margin = (value - cost) / cost * 100

				if (!isNaN(margin))
					elementMargins[key] = margin.toFixed(1) + "%"
				else
					elementMargins[key] = ""
			})

			totalMargin = (totalValue - totalCost) / totalValue * 100

			return [
				{
					elementLabel: "Materials",
					elementCost: "$ " + elementCosts.materials.toCurrency(),
					elementCostRaw: elementCosts.materials,
					elementAdjustmentValue: "$ " + elementAdjustmentValues.materials.toCurrency(),
					elementMarkupValue: "$ " + elementMarkupValues.materials.toCurrency(),
					elementValue: "$ " + elementValues.materials.toCurrency(),
					elementValueRaw: elementValues.materials,
					elementMargin: elementMargins.materials
				},
				{
					elementLabel: "Labour",
					elementCost: "$ " + elementCosts.labour.toCurrency(),
					elementCostRaw: elementCosts.labour,
					elementAdjustmentValue: "$ " + elementAdjustmentValues.labour.toCurrency(),
					elementMarkupValue: "$ " + elementMarkupValues.labour.toCurrency(),
					elementValue: "$ " + elementValues.labour.toCurrency(),
					elementValueRaw: elementValues.labour,
					elementMargin: elementMargins.labour
				},
				{
					elementLabel: "Equipment",
					elementCost: "$ " + elementCosts.equipment.toCurrency(),
					elementCostRaw: elementCosts.equipment,
					elementAdjustmentValue: "$ " + elementAdjustmentValues.equipment.toCurrency(),
					elementMarkupValue: "$ " + elementMarkupValues.equipment.toCurrency(),
					elementValue: "$ " + elementValues.equipment.toCurrency(),
					elementValueRaw: elementValues.equipment,
					elementMargin: elementMargins.equipment
				},
				{
					elementLabel: "Supplier Quotes",
					elementCost: "$ " + elementCosts['supplier-quotes'].toCurrency(),
					elementCostRaw: elementCosts['supplier-quotes'],
					elementAdjustmentValue: "$ " + elementAdjustmentValues['supplier-quotes'].toCurrency(),
					elementMarkupValue: "$ " + elementMarkupValues['supplier-quotes'].toCurrency(),
					elementValue: "$ " + elementValues['supplier-quotes'].toCurrency(),
					elementValueRaw: elementValues['supplier-quotes'],
					elementMargin: elementMargins['supplier-quotes']
				},
				{
					elementLabel: "Additional Factors",
					elementCost: "0.00",
					elementCostRaw: 0,
					elementAdjustmentValue: "$ 0.00",
					elementMarkupValue: "$ 0.00",
					elementValue: "$ " + elementValues['additional-factors'].toCurrency(),
					elementValueRaw: elementValues['additional-factors'],
					elementMargin: elementMargins['additional-factors']
				},
				{
					elementLabel: "<strong>Total</strong>",
					elementCost: "<strong>$ " + this.totalCost.toCurrency() + "</strong>",
					elementAdjustmentValue: "<strong>$ " + totalAdjustment.toCurrency() + "</strong>",
					elementMarkupValue: "<strong>$ " + totalMarkup.toCurrency() + "</strong>",
					elementValue: "<strong>$ " + this.totalValue.toCurrency() + "</strong>",
					elementMargin: "<strong>" + this.totalMargin.toFixed(1) + "%</strong>",
					footer:true
				}
			]
		}
	},
	methods: {

	},
	created() {
		let vm = this
		this.fetchAllComponents(this.bidId)

	}
}
</script>